import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Minus, Maximize2, Minimize2, ZoomIn, ZoomOut, RotateCcw, Loader2, GripVertical } from 'lucide-react';
import { renderPdfToPages } from '../utils/pdfjsLoader';

const MIN_SCALE = 0.25;
const MAX_SCALE = 5;
const DEFAULT_SCALE = 0.46;

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

/**
 * Floating PDF viewer that coexists with the form behind it.
 * - Desktop: draggable + resizable PIP window
 * - Mobile: bottom sheet with drag-to-resize handle
 * - Supports pinch-to-zoom, scroll, +/-, double-tap
 */
const FloatingPDFViewer = ({ pdfUrl, title, onClose, onMinimize }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pages, setPages] = useState([]);
  const [scaleDisplay, setScaleDisplay] = useState(DEFAULT_SCALE);
  const [baseSize, setBaseSize] = useState({ width: 0, height: 0 });
  const [mobile, setMobile] = useState(isMobile());
  const [isMaximized, setIsMaximized] = useState(false);

  // Window geometry (desktop only)
  const initialW = Math.min(560, Math.floor(window.innerWidth * 0.45));
  const initialH = Math.min(700, Math.floor(window.innerHeight * 0.7));
  const [geom, setGeom] = useState({
    x: window.innerWidth - initialW - 20,
    y: Math.max(20, Math.floor(window.innerHeight * 0.15)),
    w: initialW,
    h: initialH,
  });

  // Mobile bottom-sheet height (px from bottom)
  const [sheetHeight, setSheetHeight] = useState(Math.floor(window.innerHeight * 0.55));

  const viewportRef = useRef(null);
  const sizerRef = useRef(null);
  const contentRef = useRef(null);
  const scaleRef = useRef(DEFAULT_SCALE);
  const gestureRef = useRef(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const sheetResizeRef = useRef(null);

  // Responsive
  useEffect(() => {
    const onResize = () => setMobile(isMobile());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Load PDF
  useEffect(() => {
    if (!pdfUrl) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setPages([]);
    scaleRef.current = DEFAULT_SCALE;
    setScaleDisplay(DEFAULT_SCALE);

    (async () => {
      try {
        const rendered = await renderPdfToPages(pdfUrl, { renderScale: 2.0 });
        if (!cancelled) {
          setPages(rendered);
          setIsLoading(false);
        }
      } catch (e) {
        console.error('PDF load error:', e);
        if (!cancelled) {
          setError('Erro ao carregar PDF: ' + (e?.message || e));
          setIsLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [pdfUrl]);

  // Compute base size to fit viewport width
  useEffect(() => {
    if (pages.length === 0 || !viewportRef.current) return;
    const vp = viewportRef.current;
    const vpWidth = vp.clientWidth - 24;
    const maxPageWidth = Math.max(...pages.map(p => p.width));
    const fit = Math.min(1.5, vpWidth / maxPageWidth);
    const totalHeight = pages.reduce((sum, p) => sum + p.height * fit, 0) + (pages.length - 1) * 10 + 24;
    setBaseSize({ width: maxPageWidth * fit + 24, height: totalHeight });
  }, [pages, geom.w, geom.h, sheetHeight, mobile, isMaximized]);

  // Apply scale
  const applyScale = useCallback((newScale, scrollAdjust) => {
    const s = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
    scaleRef.current = s;
    if (contentRef.current) contentRef.current.style.transform = `scale(${s})`;
    if (sizerRef.current && baseSize.width > 0) {
      sizerRef.current.style.width = `${baseSize.width * s}px`;
      sizerRef.current.style.height = `${baseSize.height * s}px`;
    }
    if (scrollAdjust && viewportRef.current) {
      viewportRef.current.scrollLeft = scrollAdjust.x;
      viewportRef.current.scrollTop = scrollAdjust.y;
    }
    setScaleDisplay(s);
  }, [baseSize]);

  useEffect(() => { if (baseSize.width > 0) applyScale(scaleRef.current); }, [baseSize, applyScale]);

  // Zoom centered at viewport point
  const zoomAt = useCallback((newScale, vx, vy) => {
    const vp = viewportRef.current;
    const sizer = sizerRef.current;
    if (!vp) return;
    const clamped = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
    const cur = scaleRef.current;
    const sizerRect = sizer?.getBoundingClientRect();
    const vpRect = vp.getBoundingClientRect();
    const curOffsetX = sizerRect ? sizerRect.left - vpRect.left + vp.scrollLeft : 0;
    const curOffsetY = sizerRect ? sizerRect.top - vpRect.top + vp.scrollTop : 0;
    const contentX = (vx + vp.scrollLeft - curOffsetX) / cur;
    const contentY = (vy + vp.scrollTop - curOffsetY) / cur;
    const newSizerW = baseSize.width * clamped;
    const newOffsetX = newSizerW < vp.clientWidth ? (vp.clientWidth - newSizerW) / 2 : 0;
    applyScale(clamped, {
      x: contentX * clamped + newOffsetX - vx,
      y: contentY * clamped - vy,
    });
  }, [applyScale, baseSize]);

  // Pinch & double-tap
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || pages.length === 0) return;
    let lastTap = 0;
    let lastTapPos = { x: 0, y: 0 };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const [a, b] = e.touches;
        const rect = vp.getBoundingClientRect();
        const sizerRect = sizerRef.current?.getBoundingClientRect();
        const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        const midX = (a.clientX + b.clientX) / 2 - rect.left;
        const midY = (a.clientY + b.clientY) / 2 - rect.top;
        const sx = sizerRect ? sizerRect.left - rect.left + vp.scrollLeft : 0;
        const sy = sizerRect ? sizerRect.top - rect.top + vp.scrollTop : 0;
        gestureRef.current = {
          startDist: dist, startScale: scaleRef.current,
          midX, midY, startScrollX: vp.scrollLeft, startScrollY: vp.scrollTop,
          sizerOffsetX: sx, sizerOffsetY: sy,
        };
      }
    };
    const onTouchMove = (e) => {
      if (e.touches.length === 2 && gestureRef.current) {
        e.preventDefault();
        const [a, b] = e.touches;
        const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        const g = gestureRef.current;
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, g.startScale * (dist / g.startDist)));
        const contentX = (g.midX + g.startScrollX - g.sizerOffsetX) / g.startScale;
        const contentY = (g.midY + g.startScrollY - g.sizerOffsetY) / g.startScale;
        const newSizerW = baseSize.width * newScale;
        const newOffsetX = newSizerW < vp.clientWidth ? (vp.clientWidth - newSizerW) / 2 : 0;
        applyScale(newScale, {
          x: contentX * newScale + newOffsetX - g.midX,
          y: contentY * newScale - g.midY,
        });
      }
    };
    const onTouchEnd = (e) => {
      if (e.touches.length === 0) {
        gestureRef.current = null;
        if (e.changedTouches.length === 1) {
          const t = e.changedTouches[0];
          const now = Date.now();
          const dx = t.clientX - lastTapPos.x;
          const dy = t.clientY - lastTapPos.y;
          if (now - lastTap < 300 && Math.hypot(dx, dy) < 30) {
            const rect = vp.getBoundingClientRect();
            const mx = t.clientX - rect.left;
            const my = t.clientY - rect.top;
            const cur = scaleRef.current;
            if (cur > DEFAULT_SCALE * 1.1) zoomAt(DEFAULT_SCALE, mx, my);
            else zoomAt(2.2, mx, my);
            lastTap = 0;
          } else {
            lastTap = now;
            lastTapPos = { x: t.clientX, y: t.clientY };
          }
        }
      }
    };
    vp.addEventListener('touchstart', onTouchStart, { passive: false });
    vp.addEventListener('touchmove', onTouchMove, { passive: false });
    vp.addEventListener('touchend', onTouchEnd);
    vp.addEventListener('touchcancel', onTouchEnd);
    return () => {
      vp.removeEventListener('touchstart', onTouchStart);
      vp.removeEventListener('touchmove', onTouchMove);
      vp.removeEventListener('touchend', onTouchEnd);
      vp.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [pages, applyScale, zoomAt, baseSize]);

  // ============ DRAG / RESIZE listeners (montados uma vez) ============
  useEffect(() => {
    const onMove = (e) => {
      // Só age se algum gesto estiver ativo
      if (!dragRef.current && !resizeRef.current && !sheetResizeRef.current) return;
      const point = e.touches ? e.touches[0] : e;
      if (e.cancelable && e.touches) e.preventDefault();
      if (dragRef.current) {
        const dx = point.clientX - dragRef.current.startX;
        const dy = point.clientY - dragRef.current.startY;
        const { startGeom } = dragRef.current;
        setGeom(g => ({
          ...g,
          x: Math.max(0, Math.min(window.innerWidth - g.w, startGeom.x + dx)),
          y: Math.max(0, Math.min(window.innerHeight - 40, startGeom.y + dy)),
        }));
      } else if (resizeRef.current) {
        const dx = point.clientX - resizeRef.current.startX;
        const dy = point.clientY - resizeRef.current.startY;
        const { startGeom } = resizeRef.current;
        setGeom(g => ({
          ...g,
          w: Math.max(280, Math.min(window.innerWidth - g.x, startGeom.w + dx)),
          h: Math.max(240, Math.min(window.innerHeight - g.y, startGeom.h + dy)),
        }));
      } else if (sheetResizeRef.current) {
        const dy = point.clientY - sheetResizeRef.current.startY;
        const startH = sheetResizeRef.current.startH;
        setSheetHeight(Math.max(160, Math.min(window.innerHeight - 60, startH - dy)));
      }
    };
    const onUp = () => {
      dragRef.current = null;
      resizeRef.current = null;
      sheetResizeRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    window.addEventListener('touchcancel', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      window.removeEventListener('touchcancel', onUp);
    };
  }, []);

  const onDragStart = (e) => {
    if (mobile || isMaximized) return;
    const point = e.touches ? e.touches[0] : e;
    dragRef.current = { startX: point.clientX, startY: point.clientY, startGeom: { ...geom } };
    e.preventDefault();
  };
  const onResizeStart = (e) => {
    if (mobile || isMaximized) return;
    const point = e.touches ? e.touches[0] : e;
    resizeRef.current = { startX: point.clientX, startY: point.clientY, startGeom: { ...geom } };
    e.preventDefault();
    e.stopPropagation();
  };
  const onSheetResizeStart = (e) => {
    const point = e.touches ? e.touches[0] : e;
    sheetResizeRef.current = { startY: point.clientY, startH: sheetHeight };
    e.preventDefault();
  };

  // Zoom buttons
  const zoomIn = () => { const vp = viewportRef.current; if (vp) zoomAt(scaleRef.current * 1.3, vp.clientWidth / 2, vp.clientHeight / 2); };
  const zoomOut = () => { const vp = viewportRef.current; if (vp) zoomAt(scaleRef.current / 1.3, vp.clientWidth / 2, vp.clientHeight / 2); };
  const resetZoom = () => applyScale(DEFAULT_SCALE, { x: 0, y: 0 });

  // Container style
  let containerStyle;
  if (mobile) {
    containerStyle = {
      position: 'fixed',
      left: 0, right: 0, bottom: 0,
      height: `${sheetHeight}px`,
      zIndex: 99998,
      background: '#1f2937',
      borderTopLeftRadius: 14,
      borderTopRightRadius: 14,
      boxShadow: '0 -8px 30px rgba(0,0,0,0.4)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    };
  } else if (isMaximized) {
    containerStyle = {
      position: 'fixed', inset: 0, zIndex: 99998,
      background: '#1f2937', display: 'flex', flexDirection: 'column',
    };
  } else {
    containerStyle = {
      position: 'fixed',
      left: geom.x, top: geom.y, width: geom.w, height: geom.h,
      zIndex: 99998,
      background: '#1f2937',
      borderRadius: 10,
      boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      border: '1px solid #4b5563',
    };
  }

  return (
    <div data-testid="floating-pdf-viewer" style={containerStyle}>
      <style>{`@keyframes fpvspin { to { transform: rotate(360deg); } }`}</style>

      {/* Mobile drag handle (top of sheet) */}
      {mobile && (
        <div
          onMouseDown={onSheetResizeStart}
          onTouchStart={onSheetResizeStart}
          data-testid="floating-pdf-sheet-handle"
          style={{
            height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'ns-resize', touchAction: 'none', background: '#111827', flexShrink: 0,
          }}
        >
          <div style={{ width: 44, height: 4, borderRadius: 2, background: '#6b7280' }} />
        </div>
      )}

      {/* Header */}
      <div
        onMouseDown={!mobile && !isMaximized ? onDragStart : undefined}
        onTouchStart={!mobile && !isMaximized ? onDragStart : undefined}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px',
          background: '#fff', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
          cursor: !mobile && !isMaximized ? 'move' : 'default',
          userSelect: 'none', touchAction: 'none',
        }}
      >
        {!mobile && !isMaximized && (
          <GripVertical className="h-4 w-4 text-gray-400" style={{ flexShrink: 0 }} />
        )}
        <span style={{
          fontSize: 12, fontWeight: 600, color: '#374151',
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {title || 'PDF'}
        </span>

        {/* Zoom controls */}
        <button
          data-testid="floating-pdf-zoom-out"
          onClick={zoomOut}
          disabled={isLoading || !!error}
          style={btnStyle}
        ><ZoomOut className="h-3.5 w-3.5" /></button>
        <span style={{ fontSize: 10, color: '#6b7280', minWidth: 30, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
          {Math.round(scaleDisplay * 100)}%
        </span>
        <button
          data-testid="floating-pdf-zoom-in"
          onClick={zoomIn}
          disabled={isLoading || !!error}
          style={btnStyle}
        ><ZoomIn className="h-3.5 w-3.5" /></button>
        <button
          data-testid="floating-pdf-zoom-reset"
          onClick={resetZoom}
          disabled={isLoading || !!error}
          style={btnStyle}
        ><RotateCcw className="h-3.5 w-3.5" /></button>

        {/* Window controls */}
        {!mobile && (
          <>
            <button
              data-testid="floating-pdf-minimize"
              onClick={onMinimize}
              style={{ ...btnStyle, marginLeft: 4 }}
              title="Minimizar"
            ><Minus className="h-3.5 w-3.5" /></button>
            <button
              data-testid="floating-pdf-maximize"
              onClick={() => setIsMaximized(m => !m)}
              style={btnStyle}
              title={isMaximized ? 'Restaurar' : 'Maximizar'}
            >
              {isMaximized ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </button>
          </>
        )}
        {mobile && (
          <button
            data-testid="floating-pdf-minimize"
            onClick={onMinimize}
            style={btnStyle}
            title="Minimizar"
          ><Minus className="h-3.5 w-3.5" /></button>
        )}
        <button
          data-testid="floating-pdf-close"
          onClick={onClose}
          style={{ ...btnStyle, color: '#dc2626' }}
          title="Fechar"
        ><X className="h-4 w-4" /></button>
      </div>

      {/* Viewport */}
      <div
        ref={viewportRef}
        data-testid="floating-pdf-viewport"
        style={{
          flex: 1, overflow: 'auto', background: '#374151',
          WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y',
          position: 'relative',
        }}
      >
        {isLoading ? (
          <div style={loadingStyle}>
            <Loader2 className="h-8 w-8 text-blue-400" style={{ animation: 'fpvspin 1s linear infinite' }} />
            <p style={{ color: '#d1d5db', fontSize: 12, marginTop: 8 }}>Carregando PDF...</p>
          </div>
        ) : error ? (
          <div style={loadingStyle}>
            <p style={{ color: '#fca5a5', fontSize: 12, textAlign: 'center', padding: '0 16px' }}>{error}</p>
          </div>
        ) : (
          <div
            ref={sizerRef}
            style={{
              width: baseSize.width ? `${baseSize.width}px` : '100%',
              height: baseSize.height ? `${baseSize.height}px` : 'auto',
              position: 'relative', margin: '0 auto',
            }}
          >
            <div
              ref={contentRef}
              style={{
                position: 'absolute', top: 0, left: 0,
                width: baseSize.width ? `${baseSize.width}px` : '100%',
                transform: `scale(${DEFAULT_SCALE})`, transformOrigin: '0 0',
                willChange: 'transform',
                padding: 12,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                boxSizing: 'border-box',
              }}
            >
              {pages.map((p, idx) => {
                const maxW = baseSize.width ? baseSize.width - 24 : p.width;
                const fit = Math.min(maxW / p.width, 1.5);
                return (
                  <img
                    key={idx} src={p.src} alt={`Página ${idx + 1}`} draggable={false}
                    data-testid={`floating-pdf-page-${idx + 1}`}
                    style={{
                      width: p.width * fit, height: p.height * fit, maxWidth: '100%',
                      background: '#fff', boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                      display: 'block', userSelect: 'none', pointerEvents: 'none',
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Resize handle (desktop, non-maximized) */}
      {!mobile && !isMaximized && (
        <div
          onMouseDown={onResizeStart}
          onTouchStart={onResizeStart}
          data-testid="floating-pdf-resize"
          style={{
            position: 'absolute', right: 0, bottom: 0, width: 18, height: 18,
            cursor: 'nwse-resize', touchAction: 'none',
            background: 'linear-gradient(135deg, transparent 50%, #9ca3af 50%, #9ca3af 70%, transparent 70%)',
          }}
        />
      )}
    </div>
  );
};

const btnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 26, height: 26, padding: 0,
  background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 4,
  color: '#374151', cursor: 'pointer', flexShrink: 0,
};

const loadingStyle = {
  height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexDirection: 'column',
};

export default FloatingPDFViewer;
